import { formatDate } from '@angular/common';
import { Directive, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ICallableObject, IRequestCallback } from '@df/api/request/request.service';
import { Customform, ICustomformField } from '@df/catalog/customform/customform';
import { Status } from '@df/core/status';
import { NUi } from '@df/ui/df-ui.config';
import { AbstractFormComponent } from '@df/ui/form/abstract-form.component';
import compact from 'lodash-es/compact';
import each from 'lodash-es/each';
import omit from 'lodash-es/omit';

declare module '@df/catalog/resource' {
    interface IResources {
        customform: Resource<Customform, number>;
    }
}

export interface ICustomFormCanPrintEvent {
    canPrint: boolean;
    form: Customform;
    field: ICustomformField;
    data: any;
}

export interface CustomFormSubmitEvent<T extends {}> {
    data: T;
    model: Customform;
}

export interface ICustomFormBeforeEvent {
    form: NgForm;
}

export const CUSTOMFORM_SUBMIT_BEFORE_EVENT = 'customform.submit.before';

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class CustomformComponentDef<T extends {}> extends AbstractFormComponent<T> implements OnChanges {
    submitPath = 'form/submit';

    readonly loadStatus = new Status();

    fields?: ICustomformField[];

    /**
     * Optional callback before sumission
     */
    @Output()
    beforeSubmit = new EventEmitter<CustomFormSubmitEvent<Partial<T>>>();

    /**
     * Model url
     * @type {string}
     */
    @Input({
        required: true
    })
    public identifier!: string;

    @Input()
    source?: string;

    private _model?: Customform;

    checkboxes: Partial<T> = {};

    private uiModuleConfig = inject(NUi.OPTIONS);

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        if (changes['identifier']) {
            this.loadForm();
        }
    }

    protected loadForm() {
        this.model = undefined;
        const id = this.identifier;
        if (id) {
            const url = this.modelUrl;
            this.app.request.send<Customform>(
                this.loadStatus,
                () => this.app.resources.customform.findOne({ filter: { url }, verbosity: 1 }),
                customForm => {
                    if (customForm && typeof customForm.id === 'number') {
                        const model = this.app.resources.customform.get(customForm.id);
                        const _url = model.data?.url;
                        // Checking if identifyier didn't changed during load.
                        if (_url === this.modelUrl) {
                            this.model = model;
                            this.onModelChange();
                        }
                    }
                }
            );
        } else {
            this.model = undefined;
        }
    }

    protected onModelChange(): void {
        if (this.model?.data?.redirect_link && !this.redirect) {
            this.redirect = this.model.data.redirect_link;
        }

        this.updateFields();
        this.reset();
        this.detectChanges();
    }

    private get modelUrl() {
        const id = this.identifier;
        return id.charAt[0] === '/' ? id : '/' + id;
    }

    /**
     * Allow to track and modify custom form submissions
     * @param form
     * @param onSuccess
     * @param onError
     * @param noScroll
     */
    override submit(onSuccess?: IRequestCallback<any>, onError?: IRequestCallback<any>) {
        const event: ICustomFormBeforeEvent = { form: this.form };
        this.app.events.broadcast(CUSTOMFORM_SUBMIT_BEFORE_EVENT, event);

        super.submit(onSuccess, onError);
    }

    get model(): Customform | undefined {
        return this._model;
    }

    set model(model: Customform | undefined) {
        if (this._model !== model) {
            this._model = model;
            this.reset();
        }
    }

    override dataReset(): void {
        super.dataReset();

        this.fields?.forEach(field => {
            switch (field.field_type) {
                case 'checkboxes':
                    this.data[field.cid] = field.field_options?.options
                        ? field.field_options.options.filter(i => i.checked).map(i => i.label)
                        : [];
                    break;
                case 'radio':
                    this.data[field.cid] = field.field_options?.options ? field.field_options.options.find(i => i.checked)?.label : [];
                    break;
                case 'subscription':
                    this.data[field.cid] = !!field.field_options?.options?.[0].checked;
                    break;
                case 'address':
                    this.data[field.cid] = {};
                    break;
            }
        });
    }

    protected override getSubmitAction(): ICallableObject<Partial<T>> {
        if (this.model) {
            this.beforeSubmit.emit({ data: this.data, model: this.model });
        }

        /**
         * Modification of address field data to match the request params.
         * Request params for address fields looks like that :
         * c1:"aaa"
         * c2:"bbb"
         * c3:"ccc"
         * c4:"ddd@tomandco.co.uk"
         * c5:'address'
         * c5_street:"street_value"
         * c5_city:"city_value"
         * c5_region:"region_value"
         * c5_zipcode:"zipcode_value"
         * c5_country:"country_value"
         */

        const formData = omit(this.data, this.model && this.model.data ? this.model.data.fields.map(field => field.cid) : []);

        if (this.model && this.model.data) {
            this.model.data.fields.forEach(field => {
                if (field.field_type === 'address') {
                    formData[field.cid] = 'address';
                    each(this.data[field.cid], (value, key: string) => {
                        switch (key) {
                            case 'postcode':
                                formData[field.cid + '_' + 'zipcode'] = value;
                                break;
                            case 'street_1':
                                formData[field.cid + '_' + 'street'] =
                                    value + (this.data[field.cid].street_2 ? ' ' + this.data[field.cid].street_2 : '');
                                break;
                            case 'street_2':
                                break;
                            default:
                                formData[field.cid + '_' + key] = value;
                        }
                    });
                } else if (field.field_type === 'file_upload') {
                    if (this.data[field.cid]) {
                        this.requestOptions!.fileUpload = true;
                    }
                    formData[field.cid] = this.data[field.cid];
                } else if (field.field_type === 'date') {
                    formData[field.cid] = formatDate(this.data[field.cid], this.uiModuleConfig.date.format, this.app.data.locale);
                } else if (field.field_type === 'checkboxes') {
                    const data = this.data[field.cid];

                    if (Array.isArray(data)) {
                        formData[field.cid] = (data || []).filter(i => !!i);
                    } else {
                        formData[field.cid] = this.data[field.cid];
                    }
                } else {
                    formData[field.cid] = this.data[field.cid];
                    if (field.field_type === 'subscription' && this.source) {
                        formData['source'] = this.source;
                    }
                }
            });
        }

        if (!this.model) {
            throw new Error('Model is not defined');
        }

        return {
            path: this.submitPath,
            data: {
                formId: this.model.id,
                formName: this.identifier,
                ...formData
            }
        };
    }

    /**
     * This way we can locally override showing fields
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {ICustomformField} field
     */
    canPrintField(field: ICustomformField) {
        const transport: ICustomFormCanPrintEvent = {
            form: <Customform>this.model,
            field,
            canPrint: true,
            data: this.data
        };
        this.app.events.broadcast('customform.canPrint.check', transport);
        return transport.canPrint;
    }

    onCheckboxesChange(field: ICustomformField) {
        const data = compact(this.data[field.cid] || []);

        this.checkboxes[field.cid] = data.length ? true : undefined;

        this.detectChanges();
    }

    onFileUploadChange(event, field: ICustomformField) {
        this.data[field.cid] = (event.target as HTMLInputElement).files![0];

        this.detectChanges();
    }

    trackByFieldCid(index: number, field: ICustomformField): string {
        return field.cid;
    }

    trackByIndex(index: number): number {
        return index;
    }

    private updateFields() {
        const fields = this.model?.data?.fields || [];

        this.fields = fields.filter(field => {
            switch (field.field_type) {
                case 'radio':
                case 'checkboxes':
                    if (!areOptionsValid(field)) {
                        this.app.logger.log('Custom form warning: invalid field config', field);
                        return false;
                    }
                    return true;
                default:
                    return true;
            }
        });

        function areOptionsValid(field: ICustomformField) {
            const options = field.field_options?.options;
            // all options need to have labels, at least one option is required
            if (options && Array.isArray(options) && options.length && !options.find(i => !i.label)) {
                const values = options.map(i => i.label);
                // if the same value is more then onces then we cant print the option
                if (values.find(value => values.filter(i => i === value).length > 1)) {
                    return false;
                }
                return true;
            }
            return false;
        }
    }
}
